import { createFeature, createSelector, DefaultProjectorFn, MemoizedSelector } from '@ngrx/store';

import { DoodOrderModel } from './order.model';
import { ORDER_STORE_REDUCER } from './order.reducer';
import { ORDER_STORE_KEY, ORDER_STORE_ADAPTER, OrderState, OrderErrorState } from './order.state';
import { ICartValidateItem } from '@core/models/order.model';
import { DoodProductModel, IOverridesPrices } from '@core/models/product.model';

const ORDER_STORE_FEATURE = createFeature({
  name: ORDER_STORE_KEY,
  reducer: ORDER_STORE_REDUCER,
});

export const { selectOrdersState: SELECT_ORDER_STATE, selectActive: SELECT_ORDER_ACTIVE_ID } =
  ORDER_STORE_FEATURE;

export const {
  selectAll: SELECT_ORDER_ALL,
  selectTotal: SELECT_ORDER_TOTAL,
  selectEntities: SELECT_ORDER_ENTITIES,
} = ORDER_STORE_ADAPTER.getSelectors(SELECT_ORDER_STATE);

export const SELECT_ORDER_ENTITY = (
  id: string,
): MemoizedSelector<
  Record<string, any>,
  DoodOrderModel | undefined,
  DefaultProjectorFn<DoodOrderModel | undefined>
> =>
  createSelector(SELECT_ORDER_ENTITIES, entities => {
    return entities[id];
  });

export const SELECT_ORDER_MULTIPLE_ENTITIES = (
  ids: string[],
): MemoizedSelector<Record<string, any>, DoodOrderModel[], DefaultProjectorFn<DoodOrderModel[]>> =>
  createSelector(SELECT_ORDER_ENTITIES, entities => {
    return ids.map(id => entities[id]).filter(_entity => !!_entity) as DoodOrderModel[];
  });

export const SELECT_ORDER_ERRORS = createSelector(
  SELECT_ORDER_STATE,
  (state: OrderState) => state.errors,
);

export const SELECT_ORDER_VIOLATIONS = createSelector(
  SELECT_ORDER_ERRORS,
  (errors: OrderErrorState) => errors.violations,
);

export const SELECT_ORDER_VALIDATION = createSelector(
  SELECT_ORDER_STATE,
  (state: OrderState) => state.validation,
);

export const SELECT_ORDER_IS_VALIDATING = createSelector(
  SELECT_ORDER_STATE,
  (state: OrderState) => state.isValidating,
);

export const SELECT_ORDER_ACTIVE = createSelector(
  SELECT_ORDER_ENTITIES,
  SELECT_ORDER_ACTIVE_ID,
  (entities, id) => (id ? (entities[id] ?? null) : null),
);

export type AdditionsProductRecord = Record<
  string,
  {
    product: DoodProductModel;
    finalPrice: number | undefined;
  }
>;

type SimpleProductRecord = Record<
  string,
  {
    product: DoodProductModel;
    finalPrice: number | undefined; // the simpleProduct final_price is defined in priceOverridesByProductIdByCategoryId when adding inside a declinable product (If it does not exists, it is 0)
    additionsProductById: AdditionsProductRecord;
    quantity: number;
  }
>;

export type CategoryProductRecords = Record<
  string,
  {
    simpleProductById: SimpleProductRecord;
  }
>;

export const SELECT_ORDER_VALIDATION_PRODUCTS = createSelector(SELECT_ORDER_VALIDATION, state => {
  return {
    list: state?.products ?? [],
    mappedById: state?.products?.reduce(
      (acc, product) => {
        const priceOverridesByProductIdByCategoryId = (product.price_overrides ?? []).reduce(
          (accPriceOverrides, priceOverride) => {
            const categoryId = priceOverride.store;
            if (!categoryId) {
              return accPriceOverrides;
            }
            if (!accPriceOverrides[categoryId]) {
              accPriceOverrides[categoryId] = {};
            }
            accPriceOverrides[categoryId][priceOverride.product] = priceOverride;
            return accPriceOverrides;
          },
          {} as Record<string, Record<string, IOverridesPrices>>,
        );

        acc.push({
          product,
          finalPrice: product.final_price,
          ...(product.type === 'DECLINABLE' && {
            productByCategory: mapProductCategories({
              product,
              priceOverridesByProductIdByCategoryId,
            }),
          }),
          ...(product.type === 'SIMPLE' && {
            additionProductByAdditionProductId: mapAdditionsToRecord(product),
          }),
          type: product.type as 'DECLINABLE' | 'SIMPLE',
        });
        return acc;
      },
      [] as {
        product: ICartValidateItem;
        finalPrice: number | undefined;
        /**
         * Available only for declinable product
         */
        productByCategory?: CategoryProductRecords | undefined;
        /**
         * Available only for simple product
         */
        additionProductByAdditionProductId?: AdditionsProductRecord | undefined;
        type: 'DECLINABLE' | 'SIMPLE';
      }[],
    ),
  };
});
function mapAdditionsToRecord(product: ICartValidateItem): AdditionsProductRecord | undefined {
  return (product.additions as unknown as { items: DoodProductModel[] })?.items?.reduce(
    (acc, addition) => {
      acc[addition.id] = {
        product: { ...addition, final_price: addition.final_price },
        finalPrice: addition.final_price,
      };
      return acc;
    },
    {} as AdditionsProductRecord,
  );
}

function mapProductCategories({
  product,
  priceOverridesByProductIdByCategoryId,
}: {
  product: ICartValidateItem;
  priceOverridesByProductIdByCategoryId: Record<string, Record<string, IOverridesPrices>>;
}): CategoryProductRecords | undefined {
  return product.categories?.reduce((categoryAcc, category) => {
    const categoryId = category.store_id;
    if (!categoryId) {
      return categoryAcc;
    }
    categoryAcc[categoryId] = {
      simpleProductById: category.products.reduce((simpleProductAcc, simpleProduct) => {
        if (simpleProductAcc[simpleProduct.id]) {
          simpleProductAcc[simpleProduct.id].quantity += 1;
          return simpleProductAcc;
        }
        simpleProductAcc[simpleProduct.id] = {
          additionsProductById: simpleProduct.additions.items.reduce((additionsAcc, addition) => {
            additionsAcc[addition.id] = {
              product: { ...addition, final_price: addition.final_price }, // the addition final_price seems to correspond to the final price (not override from elsewhere)
              finalPrice: addition.final_price, // the addition final_price seems to correspond to the final price (not override from elsewhere)
            };
            return additionsAcc;
          }, {} as AdditionsProductRecord),
          product: {
            ...simpleProduct,
            final_price:
              priceOverridesByProductIdByCategoryId[categoryId]?.[simpleProduct.id]
                ?.additional_price ?? 0,
          }, // the simpleProduct final_price is defined in priceOverridesByProductIdByCategoryId when adding inside a declinable product (If it does not exists, it is 0)
          quantity: 1,
          finalPrice:
            priceOverridesByProductIdByCategoryId[categoryId]?.[simpleProduct.id]
              ?.additional_price ?? 0, // the simpleProduct final_price is defined in priceOverridesByProductIdByCategoryId when adding inside a declinable product (If it does not exists, it is 0)
        };
        return simpleProductAcc;
      }, {} as SimpleProductRecord),
    };
    return categoryAcc;
  }, {} as CategoryProductRecords);
}
